import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { STAGE_LABEL, ROLE_LABELS } from '@/lib/roles';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { fmtDate, fmtDateTime } from '@/lib/format';
import { CheckCircle2, Clock, RotateCcw, Send, ShieldCheck, History, XCircle, Info } from 'lucide-react';
import { QcBadge } from '@/components/quality/QualityChecksPanel';

/**
 * Iter38 — Stage Sign-off panel. Replaces the QC-gate checklist UI on
 * ProjectDetailPage. Consumes /api/projects/:id/stage-signoffs and
 * exposes three actions per stage:
 *
 *   Submit for sign-off  → moves in_progress → awaiting_signoff (note req.)
 *   Sign off             → moves awaiting → signed_off      (note req.)
 *   Return for update    → moves signed / awaiting → returned_for_update
 *
 * Auto-advance happens server-side on sign-off; on final-stage sign-off the
 * API returns { prompt_close: true } so the parent can confirm closure.
 */
export function StageSignoffPanel({ projectId, currentStage, onAdvance }) {
  const [state, setState] = useState(null);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [qcSummary, setQcSummary] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/stage-signoffs`);
      setState(r.data);
      if (!selected) setSelected(r.data.current_stage);
    } catch (e) { toast.error(parseErr(e)); }
  };
  const loadQc = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/quality-checks/summary`);
      setQcSummary(r.data.per_stage || null);
    } catch (e) { /* silent — QC is optional context */ }
  };
  useEffect(() => { load(); loadQc(); }, [projectId]);
  // Follow the project's current stage as it changes upstream.
  useEffect(() => { if (currentStage) setSelected(currentStage); }, [currentStage]);

  if (!state) return <div className="text-[13px] text-[hsl(var(--foreground-muted))] mt-3">Loading stage sign-offs…</div>;

  const rowFor = (stage) => (state.signoffs || []).find(r => r.stage === stage);
  const canSign = (stage) => !!state.can_signoff_by_stage?.[stage];
  const canReturn = !!state.can_return_for_update;

  const doAction = async (action) => {
    if (!selected) return;
    if (!note.trim() || note.trim().length < 3) {
      toast.error('A note (min 3 chars) is required for this action.');
      return;
    }
    setBusy(true);
    try {
      if (action === 'submit') {
        await api.post(`/projects/${projectId}/stage-signoffs/${selected}/submit`, { note });
        toast.success(`Submitted ${STAGE_LABEL[selected]} for sign-off`);
      } else if (action === 'signoff') {
        const r = await api.post(`/projects/${projectId}/stage-signoffs/${selected}/signoff`, { note });
        if (r.data.prompt_close) {
          toast.success('Final stage signed off — you can close the project now.');
          if (typeof onAdvance === 'function') onAdvance({ prompt_close: true });
        } else {
          toast.success(`Signed off ${STAGE_LABEL[selected]}. Advanced to ${STAGE_LABEL[r.data.advanced_to] || r.data.advanced_to}.`);
          if (typeof onAdvance === 'function') onAdvance({ advanced_to: r.data.advanced_to });
        }
      } else if (action === 'return') {
        await api.post(`/projects/${projectId}/stage-signoffs/${selected}/return`, { reason: note });
        toast.warning(`Returned ${STAGE_LABEL[selected]} for update`);
      }
      setNote('');
      await load();
    } catch (e) { toast.error(parseErr(e)); }
    finally { setBusy(false); }
  };

  const chip = (status) => {
    const map = {
      not_started:         { c: 'chip', label: 'Not started', I: Clock },
      in_progress:         { c: 'chip chip-info', label: 'In progress', I: Clock },
      awaiting_signoff:    { c: 'chip chip-warning', label: 'Awaiting sign-off', I: Send },
      signed_off:          { c: 'chip chip-success', label: 'Signed off', I: CheckCircle2 },
      returned_for_update: { c: 'chip chip-warning', label: 'Returned for update', I: RotateCcw },
    };
    const s = map[status] || map.not_started;
    const I = s.I;
    return <span className={s.c} data-testid={`signoff-chip-${status}`}><I size={11} className="mr-1" />{s.label}</span>;
  };

  const active = rowFor(selected) || {};

  return (
    <div className="mt-3" data-testid="stage-signoff-panel">
      {/* Stage picker cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {(state.stages || []).map(s => {
          const row = rowFor(s.key) || {};
          const isCurrent = state.current_stage === s.key;
          return (
            <button key={s.key} onClick={() => setSelected(s.key)}
                    data-testid={`signoff-select-${s.key}`}
                    className={`border rounded-md p-3 text-left ${selected === s.key ? 'ring-1 ring-[hsl(var(--primary))]' : ''} bg-[hsl(var(--card))]`}
                    style={{ borderColor: 'hsl(var(--border))' }}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="font-medium text-[14px]">{s.label}</div>
                  <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))] mt-0.5">
                    Any assigned project member can sign
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {isCurrent && <span className="chip chip-accent text-[10px]">Current</span>}
                  {chip(row.status || 'not_started')}
                  {qcSummary && qcSummary[s.key] && (
                    <QcBadge done={qcSummary[s.key].done}
                              total={qcSummary[s.key].total}
                              testId={`signoff-qc-badge-${s.key}`} />
                  )}
                </div>
              </div>
              {row.signed_off_at && (
                <div className="text-[11px] text-[hsl(var(--foreground-subtle))] mt-1">
                  Signed by <b>{row.signed_off_by}</b> · {fmtDate(row.signed_off_at)}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Action panel for selected stage */}
      {selected && (
        <div className="border rounded-xl bg-[hsl(var(--card))] p-4 mt-4"
             style={{ borderColor: 'hsl(var(--border))' }}
             data-testid="signoff-action-panel">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="font-heading font-semibold">Sign-off: {STAGE_LABEL[selected]}</div>
              <div className="text-[12px] text-[hsl(var(--foreground-muted))]">
                {chip(active.status || 'not_started')}
                <span className="ml-2">Any assigned project member can sign this stage off.</span>
              </div>
            </div>
            <Link to={`/projects/${projectId}/qc-history`}
                  className="chip inline-flex items-center gap-1"
                  data-testid="signoff-qc-history-link">
              <History size={11} /> QC gate history
            </Link>
          </div>

          {active.returned_reason && active.status === 'returned_for_update' && (
            <div className="border rounded-md p-3 mb-3 text-[13px] bg-[hsl(var(--surface-1))]"
                 style={{ borderColor: 'hsl(var(--border))' }}
                 data-testid="signoff-return-banner">
              <div className="font-medium mb-0.5 flex items-center gap-1"><XCircle size={12} /> Returned for update</div>
              <div className="whitespace-pre-wrap">{active.returned_reason}</div>
              <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))] mt-1">
                by {active.returned_by} · {fmtDateTime(active.returned_at)}
              </div>
            </div>
          )}

          {/* Iter45 — Optional QC completion context. READ-ONLY. Advisory. Never blocks sign-off. */}
          {qcSummary && qcSummary[selected] && (
            <div className="border rounded-md px-3 py-2 mb-3 text-[12.5px] flex items-center gap-2 bg-[hsl(var(--surface-1))]"
                 style={{ borderColor: 'hsl(var(--border))' }}
                 data-testid={`signoff-qc-context-${selected}`}>
              <Info size={12} className="text-[hsl(var(--foreground-muted))]" />
              <span className="text-[hsl(var(--foreground-muted))]">
                Optional quality checks:
              </span>
              <QcBadge done={qcSummary[selected].done}
                        total={qcSummary[selected].total}
                        testId={`signoff-qc-context-badge-${selected}`} />
              <Link to={`/projects/${projectId}#quality`}
                    className="text-[hsl(var(--foreground-muted))] underline text-[11.5px] ml-auto"
                    data-testid={`signoff-qc-context-link-${selected}`}>
                Open panel
              </Link>
              <span className="text-[11px] text-[hsl(var(--foreground-subtle))]">
                — advisory only, does not gate sign-off.
              </span>
            </div>
          )}

          <label className="text-[12px] text-[hsl(var(--foreground-muted))]">Note (required for every action)</label>
          <Textarea rows={3}
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="Explain the work completed, or the reason for returning."
                    data-testid="signoff-note" />

          <div className="mt-3 flex flex-wrap gap-2 justify-end">
            {active.status !== 'signed_off' && (
              <Button variant="outline" onClick={() => doAction('submit')} disabled={busy || active.status === 'awaiting_signoff'} data-testid="signoff-submit-btn">
                <Send size={13} className="mr-1" />Submit for sign-off
              </Button>
            )}
            {canReturn && (active.status === 'signed_off' || active.status === 'awaiting_signoff') && (
              <Button variant="outline" onClick={() => doAction('return')} disabled={busy} data-testid="signoff-return-btn">
                <RotateCcw size={13} className="mr-1" />Return for update
              </Button>
            )}
            <Button onClick={() => doAction('signoff')}
                    disabled={busy || active.status === 'signed_off' || !canSign(selected)}
                    data-testid="signoff-approve-btn">
              <ShieldCheck size={13} className="mr-1" />Sign off
            </Button>
          </div>

          {!canSign(selected) && (
            <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))] mt-2">
              You are not assigned to this project, so you cannot sign this stage off. Ask an assigned lead or team member.
            </div>
          )}

          {/* History */}
          {(active.history || []).length > 0 && (
            <div className="mt-4" data-testid="signoff-history">
              <div className="text-[12px] font-medium text-[hsl(var(--foreground-muted))] mb-2">Activity</div>
              <div className="space-y-1.5">
                {(active.history || []).slice().reverse().map((h, i) => (
                  <div key={i} className="text-[12.5px] flex items-start gap-2">
                    <span className="chip text-[10px]">{h.event}</span>
                    <div className="flex-1">
                      <div className="text-[hsl(var(--foreground))]">{h.note || h.reason || <span className="text-[hsl(var(--foreground-subtle))]">—</span>}</div>
                      <div className="text-[11px] text-[hsl(var(--foreground-subtle))]">
                        {h.by_name || h.by}{h.by_role ? ` · ${h.by_role.replace(/_/g, ' ')}` : ''} · {fmtDateTime(h.at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
